import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, Users, Calendar } from "lucide-react";
import { eventService } from "@/services/event.service";
import { DatePicker } from "@/components/ui/date-picker";
import { useDashboard } from "@/hooks/useDashboard";

export default function EventsDashboard() {
  const [searchParams] = useSearchParams();
  const url = searchParams.get('url');
  const eventoId = searchParams.get('eventoId');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  // Initialize dates from event or defaults
  useEffect(() => {
    const initializeDates = async () => {
      let initialDataInicio = '';
      let initialDataFim = '';

      if (eventoId) {
        try {
          const eventoData = await eventService.getEventById(eventoId);
          if (eventoData) {
            initialDataFim = eventoData.dataFim;
            const fimDate = new Date(eventoData.dataFim);
            const inicioDate = new Date(fimDate);
            inicioDate.setDate(inicioDate.getDate() - 7);
            initialDataInicio = inicioDate.toISOString().split('T')[0];
          }
        } catch (error) {
          console.error('Erro ao carregar evento:', error);
        }
      }

      if (!initialDataInicio || !initialDataFim) {
        const hoje = new Date();
        const semanaPassada = new Date(hoje);
        semanaPassada.setDate(semanaPassada.getDate() - 7);
        initialDataInicio = semanaPassada.toISOString().split('T')[0];
        initialDataFim = hoje.toISOString().split('T')[0];
      }

      setDataInicio(initialDataInicio);
      setDataFim(initialDataFim);
    };

    initializeDates();
  }, [eventoId]);

  const { data: dashboardData, isLoading } = useDashboard(dataInicio, dataFim);

  // Derive display data from query result
  const statusTotals = {
    na_fila: dashboardData?.statusBreakdown?.waiting ?? 0,
    concluido: dashboardData?.statusBreakdown?.completed ?? 0,
    desistente: dashboardData?.statusBreakdown?.abandoned ?? 0,
    ausente: dashboardData?.statusBreakdown?.absent ?? 0,
    cancelado: dashboardData?.statusBreakdown?.cancelled ?? 0,
  };

  const mecanicasData = (dashboardData?.queueDistribution ?? []).map(item => ({
    mecanicaId: item.queueId,
    mecanicaNome: item.queueName,
    totalInscritos: item.count,
  }));

  const participacaoData = (dashboardData?.hourlyParticipation ?? []).map(item => ({
    hora: item.hour,
    participantes: item.count,
  }));

  const formatNumber = (num: number) => {
    return num.toLocaleString('pt-BR');
  };

  const getMaxValue = (data: { participantes: number }[]) => {
    if (data.length === 0) return 0;
    return Math.max(...data.map(d => d.participantes));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-64 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-xl"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-muted rounded-xl"></div>
            <div className="h-80 bg-muted rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius)] bg-brand-logo-1/10">
            <BarChart3 className="text-brand-logo-1" size={20} />
          </div>
          <div>
            <h1 className="text-[16px] font-bold text-foreground">Dashboard da Solução</h1>
            <p className="text-[13px] text-muted-foreground">
              Visão geral do desempenho e estatísticas em tempo real
            </p>
            {url && (
              <p className="text-sm text-brand-logo-1 mt-1">
                Conectado a: {decodeURIComponent(url)}
              </p>
            )}
          </div>
        </div>

        {/* Date Selectors */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex flex-col w-full sm:w-auto">
            <label className="text-sm text-muted-foreground mb-1">Data de Início</label>
            <DatePicker
              value={dataInicio}
              onChange={(date) => setDataInicio(date)}
              placeholder="Selecione a data de início"
            />
          </div>
          <div className="flex flex-col w-full sm:w-auto">
            <label className="text-sm text-muted-foreground mb-1">Data de Fim</label>
            <DatePicker
              value={dataFim}
              onChange={(date) => setDataFim(date)}
              placeholder="Selecione a data de fim"
            />
          </div>
        </div>
      </div>

      {/* BigDumpNumbers - Status Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="text-primary" size={24} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Na Fila</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(statusTotals.na_fila)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-brand-green/10 rounded-full flex items-center justify-center">
              <Users className="text-status-ok" size={24} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Concluídos</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(statusTotals.concluido)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-brand-attention/10 rounded-full flex items-center justify-center">
              <Users className="text-status-attention" size={24} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Desistentes</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(statusTotals.desistente)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
              <Users className="text-muted-foreground" size={24} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ausentes</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(statusTotals.ausente)}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-brand-danger/10 rounded-full flex items-center justify-center">
              <Users className="text-status-danger" size={24} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cancelados</p>
              <p className="text-2xl font-bold text-foreground">{formatNumber(statusTotals.cancelado)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Inscritos por Mecânica */}
        <div className="bg-card p-4 sm:p-6 rounded-xl border border-border">
          <h3 className="text-[16px] font-semibold text-foreground mb-6">Inscritos por Mecânica</h3>
          <div className="space-y-4">
            {mecanicasData.map((mecanica, index) => {
              const maxValue = Math.max(...mecanicasData.map(m => m.totalInscritos));
              const percentage = maxValue > 0 ? (mecanica.totalInscritos / maxValue) * 100 : 0;
              const colors = ['bg-brand-logo-1', 'bg-brand-logo-2', 'bg-brand-logo-3', 'bg-brand-green'];

              return (
                <div key={mecanica.mecanicaId} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium">{mecanica.mecanicaNome}</span>
                    <span className="text-muted-foreground">{formatNumber(mecanica.totalInscritos)}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-1000 ${colors[index % colors.length]}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Participantes por Hora */}
      <div className="bg-card p-6 rounded-xl border border-border">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="text-brand-logo-1" size={24} />
          <h3 className="text-[16px] font-semibold text-foreground">Participantes por Hora</h3>
        </div>

        <div className="space-y-4">
          {participacaoData.map((hora) => {
            const maxValue = getMaxValue(participacaoData);
            const percentage = maxValue > 0 ? (hora.participantes / maxValue) * 100 : 0;

            return (
              <div key={hora.hora} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-foreground font-medium">{hora.hora}</span>
                  <span className="text-muted-foreground">{formatNumber(hora.participantes)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-1000 bg-gradient-to-r from-brand-logo-1 to-primary"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
